// Convert — every balance the desk can convert, on one screen (12 Sept 2026,
// evening; titled "Convert" since 14 Sept — SWEEP-SCOPE §5.9: every button on
// it says "Convert to X", and "swap" also meant the P2P return leg and the
// retired chain swap. One word, one meaning. The file and the /swap route
// keep their names; the nav, top bar and palette rename is Phase D).
//
// Operator, verbatim: "give me swap screen, for all customer/treasury all
// accounts on left side tab — to convert their balances." Until now a party's
// box converted from its own page (/parties/:slug/convert) and a desk account
// could not convert at all except as a Book trade typed by hand. This screen
// lists both kinds on the left — every party whose box is not square, every
// desk account holding something — and puts the right form on the right:
// the party form (ConvertBalanceForm — on account, nothing moves) or the
// desk-account form (TreasurySwapForm — a settled trade with whoever changed
// the money). Neither form is new arithmetic; each posts what its own screen
// posts.
//
// The selection lives in the URL (?party=<slug> · ?rail=<slug>&asset=<ASSET>)
// so a row can be linked to and survives a reload; ?entity= is the desk, as
// on every screen with a DeskSwitch. Switching desk clears the selection —
// the row belonged to the other desk's books.
//
// 21 Oct 2026 audit (operator: "I need clean view for operator"): a party row
// no longer carries a Customer / Merchant tag (the panel heading names the
// kind once, when one is picked); two desk accounts under one name read
// "Office cash · EUR" as the account picker does; the desk panel's heading is
// the one place the account is named (the form no longer repeats it as From).
//
// 22 Oct 2026 design-system sweep: a failed read says so and offers Try again instead of standing beside "Nothing to convert.";
// the empty states are EmptyState (an empty search offers Clear search); a zero wallet's chain or venue reading is the line
// under its name at every width (it left the name 54px at lg: "Cold wa..."), and the panel for such a wallet is that reading and
// the door to book it, not a second sentence naming the account.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, type PartySummary, type TreasuryRow } from '../lib/api';
import { ScreenHeader } from '../components/ScreenHeader';
import { DeskSwitch, useDesk, type Desk } from '../components/DeskSwitch';
import { Pending } from '../components/Pending';
import { Name } from '../components/Name';
import { EmptyState, ReadFailed, StateLine, TAP } from '../components/kit';
import { ConvertBalanceForm, ConvertAbout } from './ConvertBalance';
import { TreasurySwapForm } from './TreasurySwapForm';
import { Section } from '../components/Section';
import { UnbookedTrades } from '../components/UnbookedTrades';
import {
  partyName, partyKindLabel, partyBoxWords, railName, railKindGroupLabel, railKindOrder, sameAsset, foldAsset, dustFor,
} from '../lib/naming';
import { displayAsset, fmtAmount, fmtQuantity } from '../lib/format';
import { BALANCES_UNREAD, balancesUnread } from '../lib/balances-read';
import { rosterCut } from '../lib/roster-cut';

type Picked =
  | { kind: 'party'; slug: string }
  | { kind: 'rail'; slug: string; asset: string }
  | null;

/** "usdt-trc", "USDT·TRC", "usdttrc" → "usdttrc": one spelling for the asset
 *  match in the search box (RailPicker's rule). */
const norm = (x: string) => x.toLowerCase().replace(/[^a-z0-9]/g, '');

/** An exchange account whose venue holds more than dust of the asset — worth
 *  listing even when the books hold nothing on it yet. */
const venueHolds = (r: TreasuryRow): boolean =>
  r.treasuryKind === 'exchange' && typeof r.venue_balance === 'number' && r.venue_balance > dustFor(r.asset);
/** A wallet the chain shows money on that the books hold nothing of — the
 *  BFF's outside reading (19 Sept 2026, Hot wallet · Base opened with
 *  1,56,742 USDC pending and nothing booked: "hot wallet base not visible
 *  here & maybe many similar places"). Listed like an exchange the venue
 *  holds a coin on, for the same reason: the money is there, only the
 *  booking is missing, and the row says so. */
const chainHolds = (r: TreasuryRow): boolean => r.outside?.kind === 'chain';
/** The muted second line under such a row: "1,56,742 USDC on chain, not yet
 *  booked" — the BFF's words for where the money is. */
const chainWords = (r: TreasuryRow): string =>
  `${fmtQuantity(r.outside?.amount, r.asset)} ${displayAsset(r.asset)} ${r.outside?.words ?? ''}`;

export function Swap() {
  const [entity] = useDesk();
  const [params, setParams] = useSearchParams();
  const picked: Picked = useMemo(() => {
    const party = params.get('party');
    const rail = params.get('rail');
    const asset = params.get('asset');
    if (party) return { kind: 'party', slug: party };
    if (rail && asset) return { kind: 'rail', slug: rail, asset };
    return null;
  }, [params]);
  // The selection is the URL. Replace, not push: walking Back through every
  // row tapped is not a history anyone wants.
  const choose = (next: Picked) => {
    const p = new URLSearchParams();
    if (entity !== 'dubaiotc') p.set('entity', entity);
    if (next?.kind === 'party') p.set('party', next.slug);
    if (next?.kind === 'rail') { p.set('rail', next.slug); p.set('asset', next.asset); }
    setParams(p, { replace: true });
  };
  const switchDesk = (d: Desk) => {
    const p = new URLSearchParams();
    if (d !== 'dubaiotc') p.set('entity', d);
    setParams(p, { replace: true });
  };
  const [filter, setFilter] = useState('');

  // The whole roster with balances (500 is the endpoint's ceiling — Parties'
  // rule), and the same treasury key every money screen shares.
  const parties = useQuery({
    queryKey: ['parties', 'swap', entity],
    queryFn:  () => api.listParties({ entity, with_balances: true, pageSize: 500 }),
  });
  const treasury = useQuery({
    queryKey: ['treasury', 'balances', entity],
    queryFn:  () => api.treasuryBalances(entity),
  });
  const rows = treasury.data?.rows ?? [];

  // TWO ACCOUNTS UNDER ONE NAME TELL THEM APART BY THE ASSET (21 Oct 2026
  // audit: four rows read "Office cash", three "Bitget"). RailPicker's rule:
  // a name more than one listed account shares takes the asset after it —
  // "Office cash · EUR" — and the chip stays beside the figure too (the name
  // truncates on a phone, the chip cannot: Astra, 21 Oct). Counted over every
  // account the list can show, before the search narrows it, so a name does not
  // change as the operator types.
  const sharedName = useMemo(() => {
    const n = new Map<string, number>();
    for (const r of rows) {
      if (!r.slug || !r.asset || !(r.balance !== 0 || venueHolds(r) || chainHolds(r))) continue;
      n.set(railName(r), (n.get(railName(r)) ?? 0) + 1);
    }
    return (r: TreasuryRow) => (n.get(railName(r)) ?? 0) > 1;
  }, [rows]);

  const q = filter.trim().toLowerCase();
  // PARTIES: everyone with a box that is not zero on some asset, own accounts
  // excluded (a rail is not a party — the form says so too). Alphabetical.
  const partyRows = useMemo(() =>
    (parties.data?.parties ?? [])
      .filter((p) => p.party_type !== 'own_account' && Object.values(p.balances ?? {}).some((v) => v !== 0))
      .filter((p) => !q || partyName(p).toLowerCase().includes(q) || p.slug.toLowerCase().includes(q))
      .sort((a, b) => partyName(a).localeCompare(partyName(b)))
  , [parties.data, q]);
  // DESK ACCOUNTS: every rail holding something, by kind as the RailPicker
  // groups them, alphabetical within the kind. AN EXCHANGE ACCOUNT THE VENUE
  // HOLDS A COIN ON is listed too, at zero on the books (16 Sept 2026: the
  // 2,49,980 USDC that moved into Bitget was converted on the venue before it
  // was booked, and the list showed only Bitget's USDT — "only from usdt
  // showing"). The venue's reading rides beside the books' 0 so the row says
  // why it is here; the form then points at the Chain screen to book the
  // deposit first. A WALLET THE CHAIN SHOWS MONEY ON, likewise (chainHolds).
  const railGroups = useMemo(() => {
    const code = norm(q);
    const list = rows
      .filter((r) => !!r.slug && !!r.asset && (r.balance !== 0 || venueHolds(r) || chainHolds(r)))
      .filter((r) => !q
        || railName(r).toLowerCase().includes(q)
        || (r.label ?? '').toLowerCase().includes(q)
        || (r.slug ?? '').toLowerCase().includes(q)
        || code === norm(r.asset!) || code === norm(foldAsset(r.asset)) || code === norm(displayAsset(r.asset)))
      .sort((a, b) => railName(a).localeCompare(railName(b)) || (a.asset ?? '').localeCompare(b.asset ?? ''));
    const grouped: Record<string, TreasuryRow[]> = {};
    for (const r of list) {
      const k = r.treasuryKind ?? 'other';
      (grouped[k] ??= []).push(r);
    }
    return Object.keys(grouped)
      .sort((a, b) => railKindOrder(a) - railKindOrder(b))
      .map((k) => ({ kind: k, rows: grouped[k]! }));
  }, [rows, q]);

  // What the right-hand panel is about: the picked party (from the roster,
  // or by slug alone when a link named one the list does not carry) or the
  // picked rail (its row, by exact code first, then by the fold for a typed
  // link).
  const pickedParty: PartySummary | null = picked?.kind === 'party'
    ? (parties.data?.parties ?? []).find((p) => p.slug === picked.slug) ?? null : null;
  const pickedRow: TreasuryRow | null = picked?.kind === 'rail'
    ? rows.find((r) => r.slug === picked.slug && r.asset === picked.asset)   // dialect-ok: the URL carries the row's own code
      ?? rows.find((r) => r.slug === picked.slug && sameAsset(r.asset, picked.asset)) ?? null
    : null;

  // A read that failed is not an empty list (22 Oct 2026 sweep: "Couldn't read the list" and "Nothing to convert." stood
  // together under a dead read). The first failure speaks; Try again re-asks only what failed.
  // A FAILURE CAN ARRIVE INSIDE AN HTTP 200 (Astra, 22 Oct): with_balances answers `balances_read: false` when a ledger list could
  // not be read, every row of that list then carrying `balances: {}` — the payload of a desk that owes nothing — so a party list
  // built from it is EMPTY ("Nothing to convert.") over a ledger that was never asked. It is a failed read like any other: the
  // line, Try again, and no empty claim; rows from a partial read stay (the Parties screen's rule). It is also what a refresh
  // that turns false over a good list leaves: the new answer replaces the old, and says so.
  const unreadBalances = balancesUnread(parties.data);
  const readFailure = parties.isError ? parties.error : treasury.isError ? treasury.error : unreadBalances ? BALANCES_UNREAD : null;
  const readFailed = parties.isError || treasury.isError || unreadBalances;
  const readWhat = parties.isError || treasury.isError ? 'the list' : 'the balances';
  const retryReads = () => {
    if (parties.isError || unreadBalances) void parties.refetch();
    if (treasury.isError) void treasury.refetch();
  };
  // "NOTHING TO CONVERT" IS A CLAIM, and a claim needs reads that SUCCEEDED, both, whole (Appendix B): not a failed one, not an
  // HTTP 200 that said `balances_read: false`, not one that has not answered — a first read opened OFFLINE is paused (no data,
  // not fetching), so "neither loading nor failed" is true of it. Waiting is the line above the list instead.
  // …and a roster that ran past its page (`more`, past 500) is not a whole one either: it lists the first of them, and says so.
  const rosterCutShort = rosterCut(parties.data);
  const listRead = parties.isSuccess && treasury.isSuccess && !unreadBalances && !rosterCutShort;
  const listEmpty = partyRows.length === 0 && railGroups.length === 0;

  // THE PHONE: a tapped row opens the form UNDER the list, off the screen
  // when the list box is full — the screen scrolls to the form so the tap
  // lands on it (19 Sept 2026). At lg+ the form is the right column.
  const formCard = useRef<HTMLElement>(null);
  const pickedKey = picked ? (picked.kind === 'party' ? `party:${picked.slug}` : `rail:${picked.slug}:${picked.asset}`) : '';
  useEffect(() => {
    if (!pickedKey || window.matchMedia('(min-width: 1024px)').matches) return;
    formCard.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [pickedKey]);

  return (
    <div className="space-y-3 lg:space-y-4">
      <ScreenHeader
        title="Convert"
        subtitle="A party's box or a desk account, one asset into another."
        help={
          <>
            <p>
              Pick a party or one of the desk&rsquo;s accounts from the list; the form for it opens beside it.
            </p>
            <ConvertAbout />
            <p>
              A desk account converts through whoever changed the money — a settled trade with that
              merchant: they take the From asset off the account and give the To asset onto the account
              it lands on. The sentence above the button reads it back. The same coin on another chain is
              two moves on Move money, not a conversion.
            </p>
            {/* Moved here from under every fills button (21 Oct 2026 audit). */}
            <p>
              On an exchange account, a day&rsquo;s fills button takes the venue&rsquo;s fee inside the
              figures; to record the fee as a cost on its own, enter it under More → Venue fee.
            </p>
          </>
        }
      >
        <DeskSwitch value={entity} onChange={switchDesk} />
      </ScreenHeader>

      {/* THE UNBOOKED TRADES (his "yes, build one list of unbooked trades, show it into convert home screen", 4 Nov 2026):
          above the chooser until something is picked. */}
      {!picked && (
        <UnbookedTrades entity={entity} flush={false} frame={(title, count, body) => (
          <Section title={title} accessory={count ? <span className="text-tg-hint">{count}</span> : undefined}>{body}</Section>
        )} />
      )}

      {/* Stacked on the phone (the list capped so the form stays reachable
          beneath it), two columns from lg: the list, then the form. */}
      <div className="space-y-3 lg:space-y-0 lg:grid lg:grid-cols-[minmax(280px,400px)_minmax(0,1fr)] lg:gap-6 lg:items-start">
        {/* THE PHONE (19 Sept 2026): the search box stays put and the ROWS
            scroll in a box of their own beneath it — the whole card used to
            scroll, so the box scrolled away with the first flick and the
            keyboard's scroll-into-view had two scrollers to satisfy. The rows
            box is capped so the form opens right under it when a row is
            tapped. At lg+ the card scrolls as it always did. */}
        <aside className="card space-y-2 lg:max-h-[80vh] lg:overflow-y-auto">
          <input
            className="input"
            placeholder="Search"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          {readFailed && <ReadFailed what={readWhat} error={readFailure} onRetry={retryReads} />}
          {(parties.isPending || treasury.isPending) && !readFailed && <Pending inline />}
          {rosterCutShort && <StateLine>Only the first {parties.data?.parties.length} parties are listed.</StateLine>}
          {listRead && listEmpty && (
            <EmptyState
              action={q ? <button type="button" className={TAP} onClick={() => setFilter('')}>Clear search</button> : undefined}
            >
              {q ? 'Nothing matches.' : 'Nothing to convert.'}
            </EmptyState>
          )}

          {!listEmpty && (
            <div className="space-y-2 max-lg:max-h-[40vh] max-lg:overflow-y-auto">
              {partyRows.length > 0 && (
                <div>
                  <p className="text-tg-hint text-meta px-1 pt-1 pb-1">Parties</p>
                  {partyRows.map((p) => {
                    const active = picked?.kind === 'party' && picked.slug === p.slug;
                    return (
                      <button
                        key={p.slug}
                        type="button"
                        onClick={() => choose({ kind: 'party', slug: p.slug })}
                        className={`w-full text-left rounded-lg px-2 py-1.5 ${active ? 'bg-tg-accent/10' : 'hover:bg-tg-secondary'}`}
                      >
                        {/* No Customer / Merchant tag on the row (21 Oct 2026
                            audit: it repeated on every one) — the panel's own
                            heading names the kind once a party is picked. */}
                        <span className="flex items-baseline gap-2 min-w-0">
                          <span className={`text-body font-medium truncate ${active ? 'text-tg-accent' : ''}`} title={partyName(p)}>{partyName(p)}</span>
                        </span>
                        <span className="block text-meta text-tg-hint">{partyBoxWords(p)}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {railGroups.length > 0 && (
                <div>
                  <p className="text-tg-hint text-meta px-1 pt-2 pb-1">Desk accounts</p>
                  {railGroups.map((g) => (
                    <div key={g.kind}>
                      <p className="text-tg-hint text-meta px-1 pt-1.5 pb-0.5 opacity-70">{railKindGroupLabel(g.kind)}</p>
                      {g.rows.map((r) => {
                        const active = picked?.kind === 'rail' && picked.slug === r.slug && sameAsset(picked.asset, r.asset);
                        return (
                          // The same vertical padding as a party row (§5.9 alignment).
                          <button
                            key={`${r.slug}-${r.asset}`}
                            type="button"
                            onClick={() => choose({ kind: 'rail', slug: r.slug!, asset: r.asset! })}
                            className={`w-full text-left rounded-lg px-2 py-1.5 ${active ? 'bg-tg-accent/10' : 'hover:bg-tg-secondary'}`}
                          >
                            <Name
                              primary={sharedName(r) ? `${railName(r)} · ${foldAsset(r.asset)}` : railName(r)}
                              trailing={
                                <>
                                  {/* The asset is the chip here on every row; two accounts that
                                      share a name carry it in the name as well ("Office cash ·
                                      EUR"), which truncates where this does not. */}
                                  <span className="text-tg-hint text-meta">{displayAsset(r.asset)}</span>
                                  {/* The balance is the figure the row is picked on: the
                                      list size on the phone, the old step down at lg. */}
                                  {' '}<span className="text-body lg:text-meta">{fmtAmount(r.balance, r.asset)}</span>
                                </>
                              }
                              className={active ? 'text-tg-accent' : ''}
                            />
                            {/* A ZERO ROW'S VENUE OR CHAIN READING is the line under the name at EVERY width (22 Oct 2026
                                sweep). At lg+ it used to sit in the figures column, which is as wide as its widest
                                line — "156,742 USDT·TRC on chain, not yet booked" left "Cold wallet · Vault" 54px
                                and read "Cold wa…". Under the name it has the row's width, and the name keeps its own. */}
                            {r.balance === 0 && venueHolds(r) && (
                              <span className="block text-meta text-tg-hint font-normal">venue {fmtQuantity(r.venue_balance, r.asset)}</span>
                            )}
                            {r.balance === 0 && chainHolds(r) && (
                              <span className="block text-meta text-tg-hint font-normal">{chainWords(r)}</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </aside>

        {/* On the phone the list IS the screen until a row is tapped: an
            empty card under it saying so was a band of nothing. At lg+ the
            right column stands with its one line, as before. */}
        <section ref={formCard} className={`card space-y-3 lg:space-y-4 ${picked ? '' : 'max-lg:hidden'}`}>
          {!picked && <EmptyState>Pick a party or account.</EmptyState>}

          {picked?.kind === 'party' && (
            <>
              <div className="flex items-baseline justify-between gap-2">
                <p className="text-body font-medium min-w-0">
                  {partyName(pickedParty ?? { slug: picked.slug })}
                  {pickedParty && <span className="text-meta text-tg-hint font-normal"> · {partyKindLabel(pickedParty)}</span>}
                </p>
                <Link
                  to={`/parties/${encodeURIComponent(picked.slug)}${entity === 'dubaiotc' ? '' : `?entity=${entity}`}`}
                  className="btn-link text-meta shrink-0"
                >
                  Open party →
                </Link>
              </div>
              {/* Keyed on the party and the desk: a new pick is a new form,
                  never one carrying the last party's boxes. */}
              <ConvertBalanceForm key={`${entity}|${picked.slug}`} slug={picked.slug} entity={entity} />
            </>
          )}

          {picked?.kind === 'rail' && (
            <>
              <p className="text-body font-medium">
                {railName(pickedRow ?? { slug: picked.slug })}
                <span className="text-meta text-tg-hint font-normal">
                  {' '}· {displayAsset(pickedRow?.asset ?? picked.asset)}{pickedRow ? ` · ${fmtAmount(pickedRow.balance, pickedRow.asset)}` : ''}
                </span>
              </p>
              {/* A WALLET AT ZERO WITH MONEY ON THE CHAIN has nothing to
                  convert yet — the same door the form opens for an exchange
                  at zero (TreasurySwapForm, "holds nothing on the books"):
                  book the deposit on the Chain screen first. Said here, at
                  the form's one call site, with the form's own words. */}
              {pickedRow && pickedRow.balance === 0 && chainHolds(pickedRow)
                ? (
                  // The heading above names the account and its 0; the line says where the money is and the way to book
                  // it (22 Oct 2026 sweep: it named the account a second time and ran to 14 words).
                  <StateLine tone="warn" action={<Link to="/chain" className={TAP}>Book the money that moved in →</Link>}>
                    {chainWords(pickedRow)}
                  </StateLine>
                )
                : (
                  <TreasurySwapForm
                    key={`${entity}|${picked.slug}|${pickedRow?.asset ?? picked.asset}`}
                    entity={entity}
                    from={{ slug: picked.slug, asset: pickedRow?.asset ?? picked.asset }}
                  />
                )}
            </>
          )}
        </section>
      </div>
    </div>
  );
}
